import React, {useCallback, useState} from 'react';
import {toast} from 'react-toastify';
import clsx from 'clsx';
import Icon from '@/shared/components/Icon';
import {apiClient} from '@/shared/services';
import type {Test, RefRangeCyclePhase} from '@/shared/types/api';

interface Props {
    order: Test;
    /** Called after a successful PATCH so the parent can refetch the order
     *  with newly-matched reference ranges. */
    onSaved?: () => void;
}

const CYCLE_PHASE_OPTIONS: {value: RefRangeCyclePhase; label: string}[] = [
    {value: 'follicular', label: 'Follicular'},
    {value: 'luteal', label: 'Luteal'},
    {value: 'midcycle', label: 'Mid-cycle'},
];

const BIOTIN_OPTIONS: {value: boolean; label: string}[] = [
    {value: false, label: 'No'},
    {value: true, label: 'Yes'},
];

/**
 * Provider-facing clinical-context controls shown in the left sidebar of
 * the first panel on the result-review page.  Lets the provider record
 * conditions at the time of the blood draw that affect which reference
 * range applies.  Persists on each toggle, then asks the parent to
 * refetch the order so newly-matched ranges render in place.
 *
 * Menopausal status is not editable here because it's a stable patient
 * attribute (stored in patient_health_profiles, not on the order).  A
 * provider-scoped patient-profile UI is planned as a follow-up.
 */
const ClinicalContextPanel: React.FC<Props> = ({order, onSaved}) => {
    const [saving, setSaving] = useState<'biotin' | 'cycle_phase' | null>(null);

    // Biotin defaults to "No" visually when unset.  NULL in the DB and
    // explicit FALSE behave identically for ref-range matching (COALESCE in
    // the SQL), so this is a UX-only collapse — clicking "No" while the
    // value is NULL is a no-op rather than an explicit write.
    const onBiotinDisplay: boolean = order.on_biotin === true;
    const cyclePhase = order.cycle_phase ?? null;

    // Cycle phase is only meaningful for female patients.  Hide the row
    // entirely when the patient's recorded sex is explicitly male.
    // Unknown / unrecorded gender shows the row (safest default — provider
    // can ignore it).  Case-insensitive to tolerate 'M' / 'm' / 'Male'.
    const isExplicitlyMale = /^m(ale)?$/i.test(order.gender ?? '');
    const showCyclePhase = !isExplicitlyMale;

    const patchFlags = useCallback(
        async (
            field: 'biotin' | 'cycle_phase',
            body: {on_biotin?: boolean | null; cycle_phase?: RefRangeCyclePhase | null}
        ) => {
            setSaving(field);
            try {
                await apiClient.patch<{id: number; on_biotin: boolean | null; cycle_phase: string | null}>(
                    `/api/orders/${order.id}/clinical-flags`,
                    body
                );
                onSaved?.();
            } catch {
                toast.error('Failed to save clinical context');
            } finally {
                setSaving(null);
            }
        },
        [order.id, onSaved]
    );

    return (
        <div>
            <p className="mb-4 text-xs text-mld-muted-text">
                Conditions at the time of this blood draw. These flags affect which reference range applies.
            </p>

            <div className="space-y-3">
                <PillStack
                    label="Biotin supplement"
                    options={BIOTIN_OPTIONS}
                    value={onBiotinDisplay}
                    saving={saving === 'biotin'}
                    onChange={(value) => {
                        if (value === onBiotinDisplay) return;
                        void patchFlags('biotin', {on_biotin: value});
                    }}
                />
                {showCyclePhase && (
                    <PillStack
                        label="Menstrual cycle"
                        options={CYCLE_PHASE_OPTIONS}
                        value={cyclePhase}
                        saving={saving === 'cycle_phase'}
                        onChange={(value) => {
                            // Toggle-to-unset: clicking the already-selected pill clears it.
                            // Without this, providers have no way to un-set a cycle phase.
                            const next = value === cyclePhase ? null : value;
                            void patchFlags('cycle_phase', {cycle_phase: next});
                        }}
                    />
                )}
            </div>
        </div>
    );
};

function PillStack<T extends string | boolean>(props: {
    label: string;
    options: ReadonlyArray<{value: T; label: string}>;
    value: T | null;
    saving: boolean;
    onChange: (value: T) => void;
}) {
    return (
        <div>
            <div className="mb-1.5 flex items-center gap-1.5">
                <span className="text-[11px] font-medium text-mld-text-secondary">{props.label}</span>
                {props.saving ? (
                    <Icon name="spinner" spin className="h-3 w-3 text-mld-muted-text" aria-label="saving" />
                ) : null}
            </div>
            <div className="flex flex-wrap gap-1">
                {props.options.map((opt) => {
                    const isActive = opt.value === props.value;
                    return (
                        <button
                            key={String(opt.value)}
                            type="button"
                            disabled={props.saving}
                            onClick={() => props.onChange(opt.value)}
                            aria-pressed={isActive}
                            className={clsx(
                                'rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition',
                                'disabled:cursor-wait disabled:opacity-60',
                                isActive
                                    ? 'border-mld-primary bg-mld-primary text-white shadow-sm'
                                    : 'border-mld-panel-border bg-transparent text-mld-menu-text hover:border-mld-primary hover:bg-mld-primary/10 hover:text-mld-primary'
                            )}>
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export default ClinicalContextPanel;
